// WeightQuestion.tsx - Verbesserte Version
import { useState, useEffect } from "react";
import {useStoreDispatch, useStoreSelector} from "../../store/hooks";
import {enterNumericQuestionAnswer, selectCurrentNumericQuestionAnswers} from "../../store/questionnaireSlice";
import { QuestionConfig } from "../../data/QuestionConfig";
import {Box, Typography, TextField, Slider, useMediaQuery} from "@mui/material";
import { useTranslation } from "react-i18next";
import {theme} from "../../theme.ts";

function convertKgToLbs(kg: number): number {
    const lbsPerKg = 2.20462;
    return kg * lbsPerKg;
}

function convertLbsToKg(lbs: number): number {
    const kgPerLbs = 0.453592;
    return lbs * kgPerLbs;
}

export function WeightQuestion({ questionConfig }: { questionConfig: QuestionConfig }) {
    const { t } = useTranslation();
    const kgConfig = questionConfig.inputConfigs![0];
    const kgMin = kgConfig.min;
    const kgMax = kgConfig.max;

    const dispatch = useStoreDispatch();
    const currentAnswers = useStoreSelector(selectCurrentNumericQuestionAnswers);

    // Initialisiere mit gespeichertem Wert oder Default
    const initialKGValue = currentAnswers["weight"] ?? kgConfig.defaultValue;

    const [KGvalue, setKGvalue] = useState<number>(initialKGValue);
    const [kgTextFieldValue, setKgTextFieldValue] = useState<string>(initialKGValue.toString());
    const [lbsTextFieldValue, setLbsTextFieldValue] = useState<string>(convertKgToLbs(initialKGValue).toFixed(1));

    const [hasUserInteracted, setHasUserInteracted] = useState<boolean>(currentAnswers["weight"] !== undefined);
    const [kgError, setKgError] = useState(false);
    const [lbsError, setLbsError] = useState(false);

    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        const storedKGValue = currentAnswers["weight"];
        if (storedKGValue !== undefined) {
            setHasUserInteracted(true);
            setKGvalue(storedKGValue);
            setKgTextFieldValue(storedKGValue.toString());
            setLbsTextFieldValue(convertKgToLbs(storedKGValue).toFixed(1));
        }
    }, [currentAnswers]);

    const handleSliderChange = (_event: Event, newValue: number | number[]) => {
        if (typeof newValue === 'number') {
            // setChangedBySlider(true);
            setKGvalue(newValue);
            setKgTextFieldValue(newValue.toString());
            setLbsTextFieldValue(convertKgToLbs(newValue).toFixed(1));
            setKgError(false);
            setLbsError(false);
            putValueInState(newValue);
        } else {
            console.warn('Unexpected number array type for newValue:', newValue);
        }

        setHasUserInteracted(true);
    };

    const handleKgTextFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = event.target.value;
        // setChangedBySlider(false);
        setKgTextFieldValue(inputValue);

        if (inputValue === "") {
            setKgError(true);
            return;
        }

        const numericValue = parseFloat(inputValue);
        if (!isNaN(numericValue)) {
            if (numericValue < kgMin || numericValue > kgMax) {
                setKgError(true);
            } else {
                setKgError(false);
                setKGvalue(numericValue);
                const lbsValue = convertKgToLbs(numericValue);
                setLbsTextFieldValue(lbsValue.toFixed(1));
                putValueInState(numericValue);
            }
        } else {
            setKgError(true);
        }

        setHasUserInteracted(true);
    };

    const handleLBSTextFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = event.target.value;
        // setChangedBySlider(false);
        setLbsTextFieldValue(inputValue);

        if (inputValue === "") {
            setLbsError(true);
            return;
        }

        const lbsValue = parseFloat(inputValue);
        if (!isNaN(lbsValue)) {
            const kgValue = convertLbsToKg(lbsValue);
            if (kgValue < kgMin || kgValue > kgMax) {
                setLbsError(true);
            } else {
                setLbsError(false);
                setKGvalue(kgValue);
                setKgTextFieldValue(kgValue.toFixed(1));
                putValueInState(kgValue);
            }
        } else {
            setLbsError(true);
        }

        setHasUserInteracted(true);
    }

    const putValueInState = (value: number) => {
        dispatch(enterNumericQuestionAnswer(["weight", value]));
    }

    const handleKgBlur = () => {
        if (kgTextFieldValue === "") {
            // Wenn das Feld leer ist, auf den Minimalwert setzen
            setKgTextFieldValue(kgMin.toString());
            setLbsTextFieldValue(convertKgToLbs(kgMin).toFixed(1));
            setKGvalue(kgMin);
            setKgError(false);
            putValueInState(kgMin);
            return;
        }

        const numericValue = parseFloat(kgTextFieldValue);
        if (isNaN(numericValue) || numericValue < kgMin) {
            // Setze auf Minimalwert
            setKgTextFieldValue(kgMin.toString());
            setLbsTextFieldValue(convertKgToLbs(kgMin).toFixed(1));
            setKGvalue(kgMin);
            setKgError(false);
            putValueInState(kgMin);
        } else if (numericValue > kgMax) {
            // Setze auf Maximalwert
            setKgTextFieldValue(kgMax.toString());
            setLbsTextFieldValue(convertKgToLbs(kgMax).toFixed(1));
            setKGvalue(kgMax);
            setKgError(false);
            putValueInState(kgMax);
        } else {
            // Formatiere den gültigen Wert
            const formattedValue = numericValue.toFixed(1);
            setKgTextFieldValue(formattedValue);
            setLbsTextFieldValue(convertKgToLbs(parseFloat(formattedValue)).toFixed(1));
            setKGvalue(parseFloat(formattedValue));
            putValueInState(parseFloat(formattedValue));
        }
    };

    const handleLBSBlur = () => {
        if (lbsTextFieldValue === "") {
            // Wenn das Feld leer ist, auf den Minimalwert setzen
            const minLbs = convertKgToLbs(kgMin);
            setKgTextFieldValue(kgMin.toString());
            setLbsTextFieldValue(minLbs.toFixed(1));
            setKGvalue(kgMin);
            setLbsError(false);
            putValueInState(kgMin);
            return;
        }

        const lbsValue = parseFloat(lbsTextFieldValue);
        if (isNaN(lbsValue)) {
            const minLbs = convertKgToLbs(kgMin);
            setKgTextFieldValue(kgMin.toString());
            setLbsTextFieldValue(minLbs.toFixed(1));
            setKGvalue(kgMin);
            setLbsError(false);
            putValueInState(kgMin);
            return;
        }

        const kgValue = convertLbsToKg(lbsValue);
        if (kgValue < kgMin) {
            // Setze auf Minimalwert
            setKgTextFieldValue(kgMin.toString());
            setLbsTextFieldValue(convertKgToLbs(kgMin).toFixed(1));
            setKGvalue(kgMin);
            setLbsError(false);
            putValueInState(kgMin);
        } else if (kgValue > kgMax) {
            // Setze auf Maximalwert
            setKgTextFieldValue(kgMax.toString());
            setLbsTextFieldValue(convertKgToLbs(kgMax).toFixed(1));
            setKGvalue(kgMax);
            setLbsError(false);
            putValueInState(kgMax);
        } else {
            // Formatiere den gültigen Wert
            const formattedKgValue = kgValue.toFixed(1);
            setKgTextFieldValue(formattedKgValue);
            setLbsTextFieldValue(lbsValue.toFixed(1));
            setKGvalue(parseFloat(formattedKgValue));
            putValueInState(parseFloat(formattedKgValue));
        }
    }

    return (
        <Box sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-around",
            my: 3,
            alignItems: "center",
        }}>
            <Box sx={{ width: "80%" }}>
                <Box sx={{
                    display: "flex",
                    gap: isMobile ? "0px" : "10px",
                    justifyContent: 'center',
                    flexDirection: isMobile ? "column" : "row",
                }}>
                    <Box>
                        <Typography variant="body1">{t('weight_kg_label')}</Typography>
                        <TextField
                            value={hasUserInteracted ? kgTextFieldValue : ""}
                            type="number"
                            variant="outlined"
                            size="small"
                            inputProps={{
                                min: kgMin,
                                max: kgMax,
                                'aria-label': t('weight_input_min'),
                                step: 0.1
                            }}
                            onChange={handleKgTextFieldChange}
                            onBlur={handleKgBlur}
                            error={kgError}
                            helperText={kgError ? t('weight_error_range') : t('weight_input_helper')}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    "& fieldset": {
                                        borderColor: kgError ? "red" : "default",
                                    },
                                },
                                width: "90px",
                            }}
                        />
                    </Box>

                    <Box>
                        <Typography variant="body1">{t('weight_lbs_label')}</Typography>
                        <TextField
                            value={hasUserInteracted ? lbsTextFieldValue : ""}
                            type="number"
                            variant="outlined"
                            size="small"
                            inputProps={{
                                min: convertKgToLbs(kgMin),
                                max: convertKgToLbs(kgMax),
                                'aria-label': t('weight_input_min'),
                                step: 0.1
                            }}
                            onChange={handleLBSTextFieldChange}
                            onBlur={handleLBSBlur}
                            error={lbsError}
                            helperText={lbsError ? t('weight_error_range') : t('weight_input_helper')}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    "& fieldset": {
                                        borderColor: lbsError ? "red" : "default",
                                    },
                                },
                                width: "90px",
                            }}
                        />
                    </Box>
                </Box>

                <Slider
                    value={KGvalue}
                    min={kgMin}
                    max={kgMax}
                    step={0.5}
                    onChange={handleSliderChange}
                    aria-label={t('weight_slider_label')}
                />
            </Box>
        </Box>
    );
}